import React, { useState } from 'react'

const DSLoadTest: React.FC = () => {
  const [testResult, setTestResult] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  const testDSLoad = async () => {
    setLoading(true)
    setTestResult(null)
    
    try {
      // Test direct database access
      const dbPath = '/bank/L11/C1/B01/DS/questions.db'
      console.log('Testing DS database load:', dbPath)
      
      const response = await fetch(dbPath)
      console.log('Response status:', response.status, response.statusText)
      
      if (!response.ok) {
        setTestResult({
          success: false,
          error: `Cannot load database: ${response.status} ${response.statusText}`,
          path: dbPath
        })
        return
      }

      // Load SQL.js
      const initSqlJs = (await import('sql.js')).default
      const SQL = await initSqlJs({
        locateFile: (file: string) => `https://sql.js.org/dist/${file}`
      })

      const arrayBuffer = await response.arrayBuffer()
      const db = new SQL.Database(new Uint8Array(arrayBuffer))

      // Get tables
      const tablesResult = db.exec("SELECT name FROM sqlite_master WHERE type='table'")
      const tableNames = tablesResult.length > 0 ? tablesResult[0].values.map(row => row[0] as string) : []
      
      if (tableNames.length === 0) {
        setTestResult({
          success: false,
          error: 'No tables found in database',
          path: dbPath
        })
        db.close()
        return
      }

      // Get first table data
      const tableName = tableNames[0]
      const dataResult = db.exec(`SELECT * FROM ${tableName} LIMIT 1`)
      
      if (dataResult.length === 0 || dataResult[0].values.length === 0) {
        setTestResult({
          success: false,
          error: `No data found in table ${tableName}`,
          path: dbPath,
          tables: tableNames
        })
        db.close()
        return
      }

      const columns = dataResult[0].columns
      const firstRow = dataResult[0].values[0]
      
      // Map columns to values
      const sampleRecord: any = {}
      columns.forEach((col, index) => {
        sampleRecord[col] = firstRow[index]
      })

      // Check for DS-specific fields
      const dsFields = {
        question_lead: sampleRecord.question_lead,
        statement_a: sampleRecord.statement_a,
        statement_b: sampleRecord.statement_b,
        statement_c: sampleRecord.statement_c,
        statement_d: sampleRecord.statement_d,
        answer_a: sampleRecord.answer_a,
        answer_b: sampleRecord.answer_b,
        answer_c: sampleRecord.answer_c,
        answer_d: sampleRecord.answer_d,
        solution_a: sampleRecord.solution_a,
        solution_b: sampleRecord.solution_b,
        solution_c: sampleRecord.solution_c,
        solution_d: sampleRecord.solution_d
      }

      setTestResult({
        success: true,
        path: dbPath,
        tables: tableNames,
        columns: columns,
        sampleRecord: sampleRecord,
        dsFields: dsFields,
        hasStatements: !!(dsFields.statement_a || dsFields.statement_b || dsFields.statement_c || dsFields.statement_d),
        hasAnswers: !!(dsFields.answer_a || dsFields.answer_b || dsFields.answer_c || dsFields.answer_d),
        hasSolutions: !!(dsFields.solution_a || dsFields.solution_b || dsFields.solution_c || dsFields.solution_d)
      })

      db.close()
      
    } catch (error: any) {
      console.error('Error testing DS load:', error)
      setTestResult({
        success: false,
        error: error.message
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">DS Database Load Test</h1>
      
      <button
        onClick={testDSLoad}
        disabled={loading}
        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 mb-6"
      >
        {loading ? 'Testing...' : 'Test DS Database Load'}
      </button>

      {testResult && (
        <div className="border border-gray-300 rounded-lg p-4">
          <div className={`text-lg font-semibold mb-4 ${testResult.success ? 'text-green-600' : 'text-red-600'}`}>
            {testResult.success ? '✅ Success' : '❌ Failed'}
          </div>

          {testResult.error && (
            <div className="text-red-600 mb-4">
              <strong>Error:</strong> {testResult.error}
            </div>
          )}

          {testResult.path && (
            <div className="mb-4">
              <strong>Database Path:</strong> {testResult.path}
            </div>
          )}

          {testResult.tables && (
            <div className="mb-4">
              <strong>Tables:</strong> {testResult.tables.join(', ')}
            </div>
          )}

          {testResult.columns && (
            <div className="mb-4">
              <strong>Columns ({testResult.columns.length}):</strong>
              <div className="text-sm text-gray-600 mt-1">
                {testResult.columns.join(', ')}
              </div>
            </div>
          )}

          {testResult.dsFields && (
            <div className="mb-4">
              <h3 className="font-semibold mb-2">DS-Specific Fields:</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium">Question Lead:</div>
                  <div className="text-sm text-gray-600 break-words">
                    {testResult.dsFields.question_lead || '[NULL]'}
                  </div>
                </div>
                
                <div>
                  <div className="text-sm font-medium">Statements:</div>
                  <div className="text-xs space-y-1">
                    <div>A: {testResult.dsFields.statement_a || '[NULL]'}</div>
                    <div>B: {testResult.dsFields.statement_b || '[NULL]'}</div>
                    <div>C: {testResult.dsFields.statement_c || '[NULL]'}</div>
                    <div>D: {testResult.dsFields.statement_d || '[NULL]'}</div>
                  </div>
                </div>
                
                <div>
                  <div className="text-sm font-medium">Answers:</div>
                  <div className="text-xs space-y-1">
                    <div>A: {testResult.dsFields.answer_a || '[NULL]'}</div>
                    <div>B: {testResult.dsFields.answer_b || '[NULL]'}</div>
                    <div>C: {testResult.dsFields.answer_c || '[NULL]'}</div>
                    <div>D: {testResult.dsFields.answer_d || '[NULL]'}</div>
                  </div>
                </div>
                
                <div>
                  <div className="text-sm font-medium">Solutions:</div>
                  <div className="text-xs space-y-1">
                    <div>A: {testResult.dsFields.solution_a ? 'Available' : '[NULL]'}</div>
                    <div>B: {testResult.dsFields.solution_b ? 'Available' : '[NULL]'}</div>
                    <div>C: {testResult.dsFields.solution_c ? 'Available' : '[NULL]'}</div>
                    <div>D: {testResult.dsFields.solution_d ? 'Available' : '[NULL]'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {testResult.success && (
            <div className="mt-4 p-3 bg-green-50 rounded">
              <div className="text-sm">
                <div>Has Statements: {testResult.hasStatements ? '✅' : '❌'}</div>
                <div>Has Answers: {testResult.hasAnswers ? '✅' : '❌'}</div>
                <div>Has Solutions: {testResult.hasSolutions ? '✅' : '❌'}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default DSLoadTest